import { memo, useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Trash2, Star, Check, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Id } from '../../convex/_generated/dataModel';

interface IGeneratedAvatar {
  _id: Id<'generatedAvatars'>;
  _creationTime: number;
  userId: Id<'users'>;
  folderId: Id<'avatarFolders'>;
  referencePersonId?: Id<'referencePersons'>;
  storageId: Id<'_storage'>;
  prompt?: string;
  aspectRatio: string;
  source?: 'generated' | 'uploaded';
  isReadyToUse: boolean;
  isDefault: boolean;
  isDeleted: boolean;
  createdAt: number;
  imageUrl: string;
  referencePersonName?: string;
}

interface IAvatarCardProps {
  avatar: IGeneratedAvatar;
  onToggleReady: (id: Id<'generatedAvatars'>) => void;
  onSetDefault: (id: Id<'generatedAvatars'>) => void;
  onDelete: (id: Id<'generatedAvatars'>) => void;
}

function AvatarCard({ avatar, onToggleReady, onSetDefault, onDelete }: IAvatarCardProps) {
  return (
    <div className="group relative bg-card rounded-lg border border-border overflow-hidden">
      <div className="aspect-[9/16]">
        <img src={avatar.imageUrl} alt={avatar.prompt} className="w-full h-full object-cover" />
      </div>

      <div className="absolute top-2 left-2 flex gap-1">
        {avatar.isDefault && (
          <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full font-medium">
            Default
          </span>
        )}
        {avatar.isReadyToUse && (
          <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full font-medium">
            Ready
          </span>
        )}
      </div>

      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="secondary"
                className="h-8 w-8"
                onClick={() => onToggleReady(avatar._id)}
              >
                <Check className={`h-4 w-4 ${avatar.isReadyToUse ? 'text-green-500' : ''}`} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {avatar.isReadyToUse ? 'Remove from ready' : 'Mark as ready to use'}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="secondary"
                className="h-8 w-8"
                onClick={() => onSetDefault(avatar._id)}
              >
                <Star
                  className={`h-4 w-4 ${avatar.isDefault ? 'text-yellow-500 fill-yellow-500' : ''}`}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Set as default</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="secondary"
                className="h-8 w-8 text-red-500 hover:text-red-600"
                onClick={() => onDelete(avatar._id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="p-2">
        <p className="text-xs text-muted-foreground line-clamp-2">{avatar.prompt}</p>
        {avatar.referencePersonName && (
          <p className="text-xs text-muted-foreground mt-1">{avatar.referencePersonName}</p>
        )}
      </div>
    </div>
  );
}

const AvatarCardMemo = memo(AvatarCard);

interface IDeleteAvatarDialogProps {
  avatarId: Id<'generatedAvatars'> | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export function DeleteAvatarDialog({ avatarId, onClose, onConfirm }: IDeleteAvatarDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
    } finally {
      setIsDeleting(false);
    }
  }, [onConfirm]);

  return (
    <AlertDialog open={avatarId !== null} onOpenChange={open => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Avatar</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this avatar? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface IAvatarGridProps {
  avatars: IGeneratedAvatar[];
  onToggleReady: (id: Id<'generatedAvatars'>) => void;
  onSetDefault: (id: Id<'generatedAvatars'>) => void;
  onDelete: (id: Id<'generatedAvatars'>) => void;
}

export function AvatarGridContent({
  avatars,
  onToggleReady,
  onSetDefault,
  onDelete,
}: IAvatarGridProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {avatars.map((avatar: IGeneratedAvatar) => (
        <AvatarCardMemo
          key={avatar._id}
          avatar={avatar}
          onToggleReady={onToggleReady}
          onSetDefault={onSetDefault}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
